/**
 * PostCallDocumentationModal — appears after every call disconnect.
 * Auto-detects the patient/PCW from the caller's phone via
 * /api/communications/match-caller. Lets the agent dismiss WITHOUT
 * logging (Dawn picked "optional, no enforcement"), but the default
 * action is to fill in a short note.
 *
 * Props:
 *   callContext  {
 *     phone:        '+14145551234',
 *     direction:    'inbound' | 'outbound',
 *     duration_sec: 142,
 *     call_sid:     'CAxxx',
 *     company_name: 'Avida Healthcare',
 *   }
 *   onClose()      called whether saved or dismissed
 */
import { useEffect, useState } from "react";
import { X, User, Phone, ArrowRight, Loader2, Plus, SkipForward, HelpCircle, UserPlus } from "lucide-react";
import { toast } from "sonner";
import CommunicationLogModal from "./CommunicationLogModal";
import SaveContactForm from "./SaveContactForm";

const API = process.env.REACT_APP_BACKEND_URL;
const authHdr = () => {
  const tok = localStorage.getItem("token");
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};

function formatPhone(p) {
  if (!p) return "";
  const d = String(p).replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return p;
}

function formatDuration(sec) {
  const m = Math.floor((sec || 0) / 60);
  const s = (sec || 0) % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Visual treatment per caller type. Clients=blue, PCWs=green,
// address-book contacts (providers/clinics/MCOs/families/…)=indigo.
function entityStyle(entityType) {
  if (entityType === "pcw") return { bg: "#10B98115", color: "#10B981" };
  if (entityType === "contact") return { bg: "#6D28D915", color: "#6D28D9" };
  return { bg: "#03286615", color: "#032866" };
}

// Row subtitle label. Contacts show their kind (e.g. "Provider").
function entityKindLabel(m) {
  if (m.entity_type === "pcw") return "PCW (staff)";
  if (m.entity_type === "contact") return m.contact_kind_label || "Contact";
  return "Client";
}

export default function PostCallDocumentationModal({ callContext, onClose }) {
  const [matches, setMatches] = useState([]);
  const [picked, setPicked] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogModal, setShowLogModal] = useState(false);
  // Manual name-search state — only used when the phone auto-match came
  // back empty. Lets the agent attach the call to a client/PCW by name
  // instead of losing the log entirely.
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  // Modal has two modes: "identify" (default — pick who called) and
  // "save-contact" (a small form to save the caller as a brand-new
  // Contact record). Dawn's ask: every call must expose a Save Contact
  // option so unknown numbers don't get lost.
  const [mode, setMode] = useState("identify");
  const phone = callContext?.phone;

  useEffect(() => {
    if (!phone) {
      setLoading(false);
      return;
    }
    fetch(`${API}/api/communications/match-caller?phone=${encodeURIComponent(phone)}`, {
      headers: { ...authHdr() },
    })
      .then((r) => (r.ok ? r.json() : { matches: [] }))
      .then((d) => {
        const ms = d.matches || [];
        setMatches(ms);
        // If there's EXACTLY ONE match, pre-pick it — most common case.
        if (ms.length === 1) setPicked(ms[0]);
      })
      .catch(() => setMatches([]))
      .finally(() => setLoading(false));
  }, [phone]);

  // Debounced name search — kicks in after 2 chars, cools 250 ms after
  // the last keystroke.
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    const t = setTimeout(() => {
      fetch(`${API}/api/communications/search-contacts?q=${encodeURIComponent(q)}`, {
        headers: { ...authHdr() },
      })
        .then((r) => (r.ok ? r.json() : { matches: [] }))
        .then((d) => setSearchResults(d.matches || []))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const proceed = () => setShowLogModal(true);

  // File the call to the Unassigned Queue when the agent can't identify
  // the caller even after searching. Uses entity_type="unknown" — the
  // backend accepts these and they surface on the /comms/unassigned page
  // where an admin can re-attribute them later.
  const fileAsUnknown = () => {
    setPicked({
      entity_type: "unknown",
      entity_id: null,
      name: "Unassigned",
    });
    setShowLogModal(true);
  };

  const contactSaved = (contact) => {
    const name = `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || "New contact";
    toast.success(`Saved ${name} to Contacts`);
    onClose();
  };

  if (showLogModal && picked) {
    return (
      <CommunicationLogModal
        entityType={picked.entity_type}
        entityId={picked.entity_id}
        entityName={picked.name}
        defaults={{
          channel: "call",
          direction: callContext?.direction || "inbound",
          from_party_phone: phone,
          from_party_name: picked.name,
          source_call_sid: callContext?.call_sid || null,
          summary: callContext?.notes || "",
        }}
        onClose={onClose}
        onSaved={onClose}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-3"
      data-testid="post-call-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-200">
        {/* Header */}
        <div
          className="px-5 py-3 flex items-center justify-between border-b-[3px]"
          style={{ borderColor: "#FFDA00", background: "linear-gradient(90deg, #032866 0%, #001A4D 100%)" }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Phone className="w-4 h-4 text-white" />
            <div>
              <h3 className="text-sm font-black text-white">Document this call</h3>
              <p className="text-[10px] text-white/70 font-mono">
                {callContext?.direction === "outbound" ? "Outbound" : "Inbound"} ·{" "}
                {formatPhone(phone)} · {formatDuration(callContext?.duration_sec)}
              </p>
            </div>
          </div>
          <button onClick={onClose} data-testid="post-call-dismiss">
            <X className="w-5 h-5 text-white/80 hover:text-white" />
          </button>
        </div>

        <div className="p-5">
          {mode === "save-contact" ? (
            <SaveContactForm
              phone={phone}
              onCancel={() => setMode("identify")}
              onSaved={contactSaved}
            />
          ) : loading ? (
            <div className="text-center py-6 text-sm text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Identifying caller…
            </div>
          ) : matches.length === 0 ? (
            <div>
              <p className="font-semibold text-slate-700 mb-1 text-sm text-center">
                No matching client, PCW, or contact found
              </p>
              <p className="text-[11px] text-slate-400 text-center mb-3">
                {formatPhone(phone)} isn&apos;t in any client, PCW, or contact record. Search by name below to attach the call, save it as a new contact, or hit Skip.
              </p>
              {/* Manual name-search — the agent picks a client / PCW / contact by name */}
              <div className="relative">
                <input
                  type="text"
                  autoFocus
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPicked(null); }}
                  placeholder="Search a client, PCW, or contact by name…"
                  className="w-full h-10 px-3 rounded-lg border-2 border-slate-200 focus:border-slate-400 outline-none text-sm"
                  data-testid="post-call-search-input"
                />
                {searching && (
                  <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-3 text-slate-400" />
                )}
              </div>
              {searchResults.length > 0 && (
                <div className="mt-2 space-y-1.5 max-h-60 overflow-y-auto">
                  {searchResults.map((m) => {
                    const active = picked?.entity_id === m.entity_id && picked?.entity_type === m.entity_type;
                    return (
                      <button
                        key={`${m.entity_type}-${m.entity_id}`}
                        onClick={() => setPicked(m)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg border-2 transition text-left"
                        style={{
                          borderColor: active ? "#032866" : "#e2e8f0",
                          background: active ? "#03286608" : "white",
                        }}
                        data-testid={`post-call-search-result-${m.entity_id}`}
                      >
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                          style={{
                            background: entityStyle(m.entity_type).bg,
                            color: entityStyle(m.entity_type).color,
                          }}
                        >
                          <User className="w-3.5 h-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">{m.name}</p>
                          <p className="text-[10px] text-slate-400 truncate">
                            {entityKindLabel(m)}
                            {m.phone && <> · <span className="font-mono">{formatPhone(m.phone)}</span></>}
                          </p>
                        </div>
                        {active && <ArrowRight className="w-4 h-4 text-slate-400" />}
                      </button>
                    );
                  })}
                </div>
              )}
              {searchQuery.trim().length >= 2 && !searching && searchResults.length === 0 && (
                <p className="mt-2 text-[11px] text-slate-400 text-center">
                  No client, PCW, or contact matches “{searchQuery.trim()}”.
                </p>
              )}
            </div>
          ) : (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                Pick who you spoke with
              </p>
              <div className="space-y-1.5">
                {matches.map((m) => {
                  const active = picked?.entity_id === m.entity_id;
                  return (
                    <button
                      key={m.entity_id}
                      onClick={() => setPicked(m)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border-2 transition text-left"
                      style={{
                        borderColor: active ? "#032866" : "#e2e8f0",
                        background: active ? "#03286608" : "white",
                      }}
                      data-testid={`post-call-match-${m.entity_id}`}
                    >
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                        style={{
                          background: entityStyle(m.entity_type).bg,
                          color: entityStyle(m.entity_type).color,
                        }}
                      >
                        <User className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{m.name}</p>
                        <p className="text-[10px] text-slate-400 truncate">
                          {entityKindLabel(m)} ·{" "}
                          <span className="font-mono">{formatPhone(m.phone)}</span>
                        </p>
                      </div>
                      {active && <ArrowRight className="w-4 h-4 text-slate-400" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {mode !== "save-contact" && (
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50 flex-wrap">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-[11px] font-bold rounded-md text-slate-500 hover:bg-slate-200 flex items-center gap-1.5"
            data-testid="post-call-skip"
          >
            <SkipForward className="w-3.5 h-3.5" /> Skip
          </button>
          {/* "Save as Contact" is available on EVERY call so unknown
              numbers can be added to the shared address book on the spot,
              not just filed to Unassigned. Pre-fills the caller phone. */}
          <button
            onClick={() => setMode("save-contact")}
            className="px-3 py-1.5 text-[11px] font-bold rounded-md text-emerald-800 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 flex items-center gap-1.5"
            data-testid="post-call-save-contact"
            title="Save this caller as a new contact"
          >
            <UserPlus className="w-3.5 h-3.5" /> Save Contact
          </button>
          {/* "File as Unknown" is the safety net when the agent truly
              can't identify the caller even after searching — puts the
              log in the Unassigned queue so it's still captured and can
              be re-attributed later. */}
          {matches.length === 0 && (
            <button
              onClick={fileAsUnknown}
              className="px-3 py-1.5 text-[11px] font-bold rounded-md text-amber-800 bg-amber-50 border border-amber-200 hover:bg-amber-100 flex items-center gap-1.5"
              data-testid="post-call-file-unknown"
              title="Save this call to the Unassigned queue for later attribution"
            >
              <HelpCircle className="w-3.5 h-3.5" /> File as Unknown
            </button>
          )}
          <button
            onClick={proceed}
            disabled={!picked}
            className="px-4 py-1.5 text-[11px] font-black rounded-md shadow-sm hover:shadow-md transition disabled:opacity-50 flex items-center gap-1.5"
            style={{ background: "#032866", color: "#FFDA00" }}
            data-testid="post-call-document"
          >
            <Plus className="w-3.5 h-3.5" /> Document call
          </button>
        </div>
        )}

        {mode !== "save-contact" && !picked && matches.length > 0 && (
          <div className="px-5 pb-3 -mt-1">
            <p className="text-[10px] text-amber-700">Pick a match above to continue, or hit Skip.</p>
          </div>
        )}
      </div>
    </div>
  );
}


